import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, ShieldCheck, User as UserIcon, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Hangouts";

const roles = [
  { key: "admin", label: "Admin", icon: Crown, color: "#7C83FF" },
  { key: "organizer", label: "Organizer", icon: ShieldCheck, color: "#8AB4F8" },
  { key: "user", label: "Member", icon: UserIcon, color: "#A0A2B3" },
];

export default function MembersPage() {
  const { family, user } = useAuth();
  const isAdmin = family?.role === "admin";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const goBack = useSafeBack("/lineage");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/families/members"); setMembers(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (uid, role) => {
    setBusy(uid);
    try {
      await api.patch(`/families/members/${uid}/role`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="sticky top-0 z-40 glass hairline px-5 py-4 flex items-center gap-3">
        <button onClick={goBack} data-testid="members-back-btn"><ArrowLeft size={22} /></button>
        <h1 className="font-serif-display text-2xl">Members & Roles</h1>
      </div>

      <div className="px-5 py-5">
        {!isAdmin && (
          <div className="card-surface rounded-xl p-3 mb-4 text-xs" style={{ color: "var(--text-secondary)" }}>
            Only Admins can change roles.
          </div>
        )}
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div>
        ) : (
          <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4">
            {members.map((m) => (
              <motion.div key={m.user_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`member-row-${m.user_id}`} className="card-surface rounded-2xl p-4 lg:h-full">
                <div className="flex items-center gap-3">
                  <Avatar name={m.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {m.name}{m.user_id === user?.id && <span className="ml-1.5 text-[10px] gold-text">(you)</span>}
                    </div>
                    <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{m.email}</div>
                  </div>
                </div>
                {isAdmin ? (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {roles.map((r) => {
                      const activeRole = m.role === r.key;
                      return (
                        <button key={r.key} onClick={() => setRole(m.user_id, r.key)}
                          disabled={busy === m.user_id || activeRole}
                          data-testid={`set-role-${r.key}-${m.user_id}`}
                          className="rounded-xl py-2 flex flex-col items-center gap-1 text-[11px] transition-colors"
                          style={activeRole
                            ? { background: "rgba(124,131,255,0.15)", border: "1px solid rgba(124,131,255,0.4)", color: r.color }
                            : { background: "var(--overlay)", border: "1px solid transparent", color: "var(--text-secondary)" }}>
                          <r.icon size={15} style={{ color: activeRole ? r.color : "var(--text-secondary)" }} />
                          {r.label}{activeRole && <Check size={10} className="absolute" style={{ marginTop: -2 }} />}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px]"
                    style={{ background: "var(--overlay)" }}>
                    {(() => { const r = roles.find((x) => x.key === m.role) || roles[2]; return (<><r.icon size={12} style={{ color: r.color }} /><span style={{ color: r.color }}>{r.label}</span></>); })()}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
